"use client";

import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  RenderIf,
  Typography,
} from "@repo/ui";
import { StarIcon, TimeIcon } from "@spoctech/icons";
import Image from "next/image";
import { useParams } from "next/navigation";
import ImagePlaceholder from "../common/ImagePlaceholder";
import { Calendar } from "lucide-react";
import Link from "next/link";
import clsx from "clsx";

interface CourseCardProps {
  coverSrc: string;
  tag?: string;
  title: string;
  slug?: string;
  accreditation_url?: string;
  accreditation_logo?: string;
  accreditation_name?: string;
  description?: string;
  totalEnroll?: string;
  ratings?: string;
  topicsCovered?: string;
  topics?: Array<string>;
  author?: string;
  date?: string;
  duration?: string;
  isAuthor?: boolean;
  readingDuration?: string;
  features?: Array<{ name: string; isHtmlDescription: boolean }>;
}

const CourseCard = ({
  coverSrc,
  tag,
  title,
  slug,
  accreditation_name,
  accreditation_logo,
  description,
  totalEnroll,
  ratings,
  author,
  date,
  duration,
  isAuthor = false,
  // features,
}: CourseCardProps) => {
  // const modifiedFeatures = useMemo(() => {
  //   if (!features) return [];
  //   return features
  //     .map((feature) =>
  //       feature.isHtmlDescription
  //         ? unOrderedListToArray(feature.name)
  //         : [feature.name]
  //     )
  //     .flat();
  // }, [features]);

  const { country } = useParams<{ country?: string }>();

  return (
    <Card className="relative !flex flex-col min-h-full !border !shadow-md">
      <RenderIf
        condition={Boolean(coverSrc)}
        fallback={<ImagePlaceholder size={163} />}
      >
        <CardMedia
          className="h-1/2 relative"
          render={
            <Image
              src={coverSrc}
              alt={title || "card-media"}
              width={360}
              height={163}
              className={clsx("w-full h-auto", {
                "aspect-auto": !isAuthor,
                "aspect-video": isAuthor,
              })}
              quality={100}
            />
          }
        />
      </RenderIf>
      <CardContent className="flex-1 relative">
        {Boolean(tag) && (
          <Box className="absolute -top-10 right-0">
            <Chip
              label={tag || ""}
              size="small"
              className="text-xs !border-none !bg-secondary-main !text-secondary-contrastText  !rounded-none !rounded-s-lg"
            />
          </Box>
        )}
        <Typography
          variant="h6"
          render={<div />}
          className="!font-bold tracking-wide"
          gutterBottom
        >
          {title}
        </Typography>
        {(ratings || totalEnroll) && (
          <Box flex alignItems="center">
            <Box flex alignItems="center">
              <StarIcon />
              <Typography render={<span />}>
                {ratings + ` (234 Ratings)`}
              </Typography>
            </Box>
            <Divider orientation="vertical" />
            <Box>
              <Typography>{totalEnroll}+ Enrolls</Typography>
            </Box>
          </Box>
        )}
        {duration && (
          <Box className="py-3">
            <List>
              {/* {modifiedFeatures?.slice(0, 2).map((item, index) => (
              <ListItem key={`course-feature-${index}`}>
                <ListItemIcon className="!text-success-main">
                  <CheckCircleIcon />
                </ListItemIcon>
                <ListItemText>{item}</ListItemText>
              </ListItem>
            ))} */}

              <ListItem>
                <ListItemIcon className="!text-primary-main">
                  <TimeIcon />
                </ListItemIcon>
                <ListItemText>
                  Duration:
                  <Typography render={<span />} className="ml-2 !font-bold">
                    {duration}
                  </Typography>
                </ListItemText>
              </ListItem>
            </List>
          </Box>
        )}

        {accreditation_name && (
          <Box flex alignItems="center" className="gap-3 mb-3">
            {accreditation_logo && (
              <Image
                src={accreditation_logo}
                width={40}
                height={40}
                alt="csm-certified"
                className="w-auto h-auto"
              />
            )}

            <Typography>{accreditation_name}</Typography>
          </Box>
        )}

        {description && <Typography>{description}</Typography>}
      </CardContent>
      <CardActions>
        <RenderIf
          condition={isAuthor}
          fallback={
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              render={
                <Link
                  href={
                    country ? `/${country}/course/${slug}` : `/course/${slug}`
                  }
                />
              }
            >
              View Course
            </Button>
          }
        >
          <Box flex className="border-t  py-4">
            <Box flex alignItems="center">
              <Typography
                variant="body1"
                className="!text-gray-500 tracking-wide ml-2 flex gap-1 items-center"
              >
                <Calendar className="text-primary-main" size={18} /> {date}
              </Typography>
            </Box>
            <Divider orientation="vertical" />
            <Box flex>
              <Typography variant="body1" className="!text-gray-500">
                By
              </Typography>
              <Typography
                variant="body1"
                className="!font-semi-bold tracking-wide ml-2"
              >
                {author}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            render={
              <a href={slug || "#"} target="_blank" rel="noopener noreferrer" />
            }
          >
            Explore now
          </Button>
        </RenderIf>
      </CardActions>
    </Card>
  );
};

export default CourseCard;
